import * as yup from 'yup';
import { useApolloClient } from '@apollo/client';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { getServerErrors } from '../../../../common/helpers';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

const editEventLocaleSchema = yup.object();

export interface EditEventLocaleFormProps {
  event: Event;
}

const EditEventLocaleForm = ({ event }: EditEventLocaleFormProps) => {
  const { t } = useTranslation();
  const { sortedLocales } = useLocale();
  const client = useApolloClient();

  const getValues = (event: Event) => ({
    locale: event.locale,
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      locale: form.values,
    },
    onSuccess: (event) => {
      // Make sure that all updated translations are refetched.
      client.resetStore();

      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventLocaleSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'locale'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('language')}</UI.Legend>
          <UI.InputDescription>
            {t('event_language_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.locale, touched: form.touched.locale }}>
          <UI.InputLabel htmlFor="locale">{t('language')}</UI.InputLabel>

          <UI.Select
            id="locale"
            {...form.select('locale', sortedLocales)}
          >
            {sortedLocales.map((locale, index) => (
              <option {...form.option(index)} key={index}>
                {t(`common:locales.${locale}`)}
              </option>
            ))}
          </UI.Select>

          <UI.ErrorMessages attribute={t('language')} errors={errors.locale} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventLocaleForm;
